/**
 * Trinetra brand icon — radar sweep.
 *
 * Composition (outside → in, back → front):
 *   1. Outer ring   — ajna chakra / outer containment. Static. r=10.
 *   2. Middle ring  — radar range mark. Static. r=6.25 (roughly halfway).
 *   3. Radar sweep  — a wedge + bright leading edge that emanates from the
 *                     iris outward to the outer ring, rotating continuously
 *                     clockwise (6s linear). Classic radar/sonar feel — the
 *                     "third eye" actively scanning its field. The wedge is
 *                     a translucent `currentColor` fill (18%); the leading
 *                     edge is a full-opacity stroke so motion reads clearly
 *                     even at 16–20px favicon sizes. The wedge passes over
 *                     the middle ring (which is fine — reinforces the
 *                     scanning-across-range-marks radar read).
 *   4. Iris ring    — inner focal point. Static. r=2.5. Sits on top of the
 *                     sweep so the wedge appears to emerge FROM behind the
 *                     iris, not through it.
 *
 * Three concentric rings, no center dot — the rings themselves carry the
 * focal weight and give the icon a clean target/reticle silhouette.
 *
 * Animation driven by `.trinetra-sweep` in app.css (respects
 * prefers-reduced-motion — the sweep halts but composition stays legible).
 *
 * Matches lucide-react's SVGProps shape — drop-in for every existing
 * `<TrinetraLoop3 className="h-8 w-8 ..." />` import across the app.
 */

import type { SVGProps } from 'react';

export function TrinetraLoop3(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {/* Outer ring — slightly inset from viewBox so the stroke never clips. */}
      <circle cx="12" cy="12" r="10" />

      {/* Middle ring — radar range mark. Static. Sits at r=6.25 (roughly
        * halfway between iris r=2.5 and outer r=10). */}
      <circle cx="12" cy="12" r="6.25" />

      {/* Rotating radar sweep. Grouped so the wedge + leading edge rotate
        * together around the svg center. The wedge spans ~30° (12 o'clock
        * to ~1 o'clock), with the leading edge at 12 o'clock. Geometry:
        *   - Inner arc:  r=2.5  (iris outer)
        *   - Outer arc:  r=9.75 (just inside the outer ring stroke)
        *   - Leading:    (12, 9.5) → (12, 2.25)
        *   - Trailing:   (13.25, 9.835) → (16.875, 3.56) [30° clockwise] */}
      <g className="trinetra-sweep">
        <path
          d="M 12 9.5 L 12 2.25 A 9.75 9.75 0 0 1 16.875 3.56 L 13.25 9.835 A 2.5 2.5 0 0 0 12 9.5 Z"
          fill="currentColor"
          fillOpacity="0.18"
          stroke="none"
        />
        {/* Leading edge — the bright radar "line" that trails the wedge. */}
        <line x1="12" y1="9.5" x2="12" y2="2.25" />
      </g>

      {/* Iris ring — static, sits on top of sweep so it's never occluded. */}
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}
